export const GUIDE_THEME_VERSION = 1;

const INTER = "'Inter', sans-serif";
const LATO = "'Lato', sans-serif";
const POPPINS = "'Poppins', sans-serif";
const PLAYFAIR = "'Playfair Display', serif";

const typography = (heading, body = INTER) => ({
  guideTitleFont: heading,
  guideTitleFontSize: "24px",
  guideTitleFontWeight: "bold",
  sectionTitleFont: heading,
  sectionTitleFontSize: "20px",
  sectionTitleFontWeight: "bold",
  introTextFont: body,
  introTextFontSize: "15px",
  introTextFontWeight: "normal",
  blockTitleFont: body,
  blockTitleFontSize: "15px",
  blockTitleFontWeight: "bold",
  blockTextFont: body,
  blockTextFontSize: "15px",
  blockTextFontWeight: "normal",
});

const colors = ({ background, surface, text, muted, heading, border }) => ({
  pageBackgroundColor: background,
  headerBackgroundColor: background,
  blockBackgroundColor: surface,
  guideTitleColor: heading,
  sectionTitleColor: heading,
  introTextColor: muted,
  blockTitleColor: heading,
  blockTextColor: text,
  borderColor: border,
  mutedTextColor: muted,
});

export const GUIDE_THEME_PRESETS = [
  {
    id: "classic",
    name: "Classic",
    description: "Structured, familiar, and timeless",
    layoutStyle: "tabs",
    defaultAccent: "#344f68",
    defaults: {
      blockStyle: "Collapsible",
      blockBorder: true,
      showIcons: true,
      areaGuideView: "list",
    },
    typography: {
      ...typography(INTER),
      guideTitleFontSize: "24px",
      sectionTitleFontSize: "19px",
      blockTitleFontSize: "15px",
      blockTitleFontWeight: "400",
    },
    colors: colors({
      background: "#f5f5f5",
      surface: "#ffffff",
      text: "#41464a",
      muted: "#6e7478",
      heading: "#252b30",
      border: "#dedede",
    }),
  },
  {
    id: "modern",
    name: "Modern",
    description: "Bold, minimal, and card-forward",
    layoutStyle: "tabs",
    defaultAccent: "#2563eb",
    defaults: {
      blockStyle: "Collapsible",
      blockBorder: false,
      showIcons: true,
      areaGuideView: "grid",
    },
    typography: {
      ...typography(POPPINS),
      guideTitleFontSize: "28px",
      sectionTitleFontSize: "21px",
      blockTitleFontWeight: "normal",
    },
    colors: {
      ...colors({
        background: "#e9f0ff",
        surface: "#ffffff",
        text: "#27364a",
        muted: "#62738a",
        heading: "#101c2c",
        border: "#d3deed",
      }),
      blockTitleColor: "#111111",
    },
  },
  {
    id: "coastal",
    name: "Coastal",
    description: "Sea-glass tones with warm sand",
    layoutStyle: "single-scroll",
    defaultAccent: "#087f86",
    defaults: {
      blockStyle: "Inline",
      blockBorder: true,
      showIcons: true,
      areaGuideView: "list",
    },
    typography: {
      ...typography(LATO, LATO),
      guideTitleFontSize: "28px",
      sectionTitleFontSize: "22px",
      blockTitleFontSize: "16px",
    },
    colors: colors({
      background: "#e4f4ef",
      surface: "#fff7e8",
      text: "#315a5b",
      muted: "#668382",
      heading: "#0f4e52",
      border: "#9fc9c5",
    }),
  },
  {
    id: "boutique",
    name: "Boutique",
    description: "Editorial serif with rich warmth",
    layoutStyle: "single-scroll",
    defaultAccent: "#8f4058",
    defaults: {
      blockStyle: "Inline",
      blockBorder: true,
      showIcons: false,
      areaGuideView: "grid",
    },
    typography: {
      ...typography(PLAYFAIR, LATO),
      guideTitleFontSize: "30px",
      guideTitleFontWeight: "600",
      sectionTitleFontSize: "23px",
      sectionTitleFontWeight: "600",
      blockTitleFont: PLAYFAIR,
      blockTitleFontSize: "17px",
      blockTitleFontWeight: "600",
    },
    colors: colors({
      background: "#f8eae4",
      surface: "#fff8f4",
      text: "#56413f",
      muted: "#806965",
      heading: "#3f272c",
      border: "#d9beb5",
    }),
  },
];

export const getGuideThemePreset = (themeId) =>
  GUIDE_THEME_PRESETS.find((theme) => theme.id === themeId) || null;

const normalizeHex = (value) => {
  const match = /^#([0-9a-f]{6})$/i.exec(value || "");
  return match ? `#${match[1].toLowerCase()}` : null;
};

const channels = (hex) => {
  const value = normalizeHex(hex);
  if (!value) return null;
  return [1, 3, 5].map((index) => parseInt(value.slice(index, index + 2), 16));
};

const luminance = (hex) => {
  const rgb = channels(hex);
  if (!rgb) return 0;
  const values = rgb.map((value) => {
    const channel = value / 255;
    return channel <= 0.03928
      ? channel / 12.92
      : Math.pow((channel + 0.055) / 1.055, 2.4);
  });
  return values[0] * 0.2126 + values[1] * 0.7152 + values[2] * 0.0722;
};

const contrast = (first, second) => {
  const lighter = Math.max(luminance(first), luminance(second));
  const darker = Math.min(luminance(first), luminance(second));
  return (lighter + 0.05) / (darker + 0.05);
};

const mix = (first, second, amount) => {
  const from = channels(first);
  const to = channels(second);
  if (!from || !to) return first;
  const values = from.map((value, index) =>
    Math.round(value + (to[index] - value) * amount),
  );
  return `#${values.map((value) => value.toString(16).padStart(2, "0")).join("")}`;
};

const ensureVisibleAccent = (accent, background) => {
  let candidate = normalizeHex(accent);
  if (!candidate) return null;
  const target = luminance(background) < 0.35 ? "#ffffff" : "#000000";
  for (let step = 0; step < 8 && contrast(candidate, background) < 3; step += 1) {
    candidate = mix(candidate, target, 0.14);
  }
  return candidate;
};

export const readableTextColor = (background) =>
  contrast(background, "#ffffff") >= 4.5 ? "#ffffff" : "#111827";

export const resolveGuideTheme = (themeId, brandAccent) => {
  const preset = getGuideThemePreset(themeId);
  if (!preset) return null;

  const accent =
    ensureVisibleAccent(brandAccent || preset.defaultAccent, preset.colors.pageBackgroundColor) ||
    preset.defaultAccent;

  return {
    ...preset.defaults,
    ...preset.typography,
    ...preset.colors,
    activeTabColor: accent,
    tabTextColor: readableTextColor(accent),
    distanceColor: accent,
    iconColor: accent,
    linkColor: accent,
  };
};

export const isGuideThemeId = (themeId) => !!getGuideThemePreset(themeId);
